import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAuthenticated as checkAuth, apiRequest } from '../services/authService';
import { ChatMessage, AIProvider, getBrowserLanguage } from '../services/api';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface HeroImageSettings {
  enabled: boolean;
  filename: string | null;
  opacity: number;
  blend_mode: string;
  blur: number;
}

/** Lightweight markdown bold renderer */
function renderBold(text: string) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
      )}
    </>
  );
}

export default function NewbuildPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Hero background
  const [heroImage, setHeroImage] = useState<HeroImageSettings | null>(null);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [initReady, setInitReady] = useState(false);
  const existingProfileRef = useRef<any>(null);

  // Fetch hero image
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/newbuild-page-image-settings`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setHeroImage(data.data);
        }
      })
      .catch(() => {});
  }, []);

  // Load existing newbuild profile if logged in & restore pending chat
  useEffect(() => {
    async function loadProfile() {
      if (checkAuth()) {
        try {
          const data = await apiRequest('/api/newbuild-profile');
          if (data.success && data.data?.profile) {
            existingProfileRef.current = data.data.profile;
          }
        } catch { /* ignore */ }

        // Check for pending chat messages saved before login
        const pendingChat = localStorage.getItem('pendingNewbuildChat');
        if (pendingChat) {
          try {
            const pendingMessages: ChatMessage[] = JSON.parse(pendingChat);
            localStorage.removeItem('pendingNewbuildChat');
            if (pendingMessages.length > 0) {
              setMessages(pendingMessages);
              setIsComplete(true);
              setSavedMessage('Chat wird wiederhergestellt und Profil gespeichert...');
              // Auto-save the pending profile
              await extractAndSaveProfileFromPending(pendingMessages);
              return; // Skip normal init
            }
          } catch {
            localStorage.removeItem('pendingNewbuildChat');
          }
        }
      }
      setInitReady(true);
    }
    loadProfile();
  }, []);

  // Start conversation when ready
  useEffect(() => {
    if (initReady && messages.length === 0) {
      startConversation();
    }
  }, [initReady]);

  // Auto-scroll chat
  useEffect(() => {
    const el = chatEndRef.current;
    if (el?.parentElement) {
      el.parentElement.scrollTop = el.parentElement.scrollHeight;
    }
    if (!isLoading && !isComplete) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, isLoading]);

  async function startConversation() {
    setIsLoading(true);
    setError(null);
    try {
      const existingProfile = existingProfileRef.current;
      const firstMessage = existingProfile
        ? 'Hallo, ich bin zurück und möchte mein Neubauprojekt besprechen.'
        : 'Hallo, ich möchte ein Neubauprojekt planen.';

      const res = await fetch(`${API_BASE}/api/newbuild-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: firstMessage }],
          newbuildProfile: existingProfile || undefined,
          language: getBrowserLanguage(),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message || 'Chat-Fehler');

      setMessages([
        { role: 'user', content: firstMessage },
        { role: 'assistant', content: data.data.message },
      ]);
      if (data.data.isComplete) setIsComplete(true);
    } catch (err: any) {
      setError(err.message || 'Verbindungsfehler');
    }
    setIsLoading(false);
    inputRef.current?.focus();
  }

  async function handleSend() {
    if (!input.trim() || isLoading || isComplete) return;

    const userMessage: ChatMessage = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const existingProfile = existingProfileRef.current;
      const res = await fetch(`${API_BASE}/api/newbuild-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedMessages,
          newbuildProfile: existingProfile || undefined,
          language: getBrowserLanguage(),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message || 'Chat-Fehler');

      const assistantMessage: ChatMessage = { role: 'assistant', content: data.data.message };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);

      if (data.data.isComplete) {
        setIsComplete(true);
        // Extract and save the newbuild profile
        await extractAndSaveProfile(finalMessages);
      }
    } catch (err: any) {
      setError(err.message || 'Verbindungsfehler');
    }
    setIsLoading(false);
  }

  /** Save pending chat from localStorage after login (skips auth check) */
  async function extractAndSaveProfileFromPending(chatMessages: ChatMessage[]) {
    setIsSaving(true);
    try {
      const data = await apiRequest('/api/newbuild-chat/extract-profile', {
        method: 'POST',
        body: JSON.stringify({
          messages: chatMessages,
          existingProfile: existingProfileRef.current || undefined,
          language: getBrowserLanguage(),
        }),
      });

      if (data.success) {
        existingProfileRef.current = data.data?.profile || existingProfileRef.current;
        try {
          const projData = await apiRequest('/api/newbuild-projects', {
            method: 'POST',
            body: JSON.stringify({
              name: data.data?.profile?.building_type
                ? `Neubau – ${data.data.profile.building_type}`
                : 'Mein Neubauprojekt',
              migrate_existing: true,
            }),
          });
          if (projData.success && projData.data?.id) {
            setCreatedProjectId(projData.data.id);
          }
        } catch (projErr: any) {
          console.error('Project creation failed:', projErr);
        }
        setSavedMessage('Neubauprofil aus vorherigem Chat gespeichert! Sie können jetzt Dokumente hochladen.');
      } else {
        setSavedMessage('Chat wiederhergestellt, aber Profil konnte nicht gespeichert werden.');
      }
    } catch (err: any) {
      console.error('Pending profile extraction failed:', err);
      setSavedMessage('Chat wiederhergestellt. Profil-Extraktion fehlgeschlagen.');
    }
    setIsSaving(false);
  }

  async function extractAndSaveProfile(chatMessages: ChatMessage[]) {
    if (!checkAuth()) {
      // Store chat messages in localStorage so they survive the login redirect
      try {
        localStorage.setItem('pendingNewbuildChat', JSON.stringify(chatMessages));
      } catch { /* ignore quota errors */ }
      setSavedMessage('Chat abgeschlossen! Melden Sie sich an oder registrieren Sie sich, um Ihr Profil zu speichern.');
      return;
    }

    setIsSaving(true);
    try {
      // Step 1: Extract and save profile to users table (apiRequest auto-refreshes expired tokens)
      const data = await apiRequest('/api/newbuild-chat/extract-profile', {
        method: 'POST',
        body: JSON.stringify({
          messages: chatMessages,
          existingProfile: existingProfileRef.current || undefined,
          language: getBrowserLanguage(),
        }),
      });

      if (data.success) {
        existingProfileRef.current = data.data?.profile || existingProfileRef.current;

        // Step 2: Auto-create a project with the extracted profile
        try {
          const projData = await apiRequest('/api/newbuild-projects', {
            method: 'POST',
            body: JSON.stringify({
              name: data.data?.profile?.building_type
                ? `Neubau – ${data.data.profile.building_type}`
                : 'Mein Neubauprojekt',
              migrate_existing: true,
            }),
          });
          if (projData.success && projData.data?.id) {
            setCreatedProjectId(projData.data.id);
          }
        } catch (projErr: any) {
          console.error('Project creation failed:', projErr);
        }

        setSavedMessage('Neubauprofil gespeichert! Sie können jetzt Dokumente hochladen.');
      } else {
        setSavedMessage('Chat abgeschlossen, aber Profil konnte nicht gespeichert werden.');
      }
    } catch (err: any) {
      console.error('Profile extraction failed:', err);
      setSavedMessage('Chat abgeschlossen. Profil-Extraktion fehlgeschlagen.');
    }
    setIsSaving(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleNewChat() {
    setMessages([]);
    setIsComplete(false);
    setSavedMessage(null);
    setCreatedProjectId(null);
    setError(null);
    setInitReady(false);
    // Reload profile then restart
    (async () => {
      if (checkAuth()) {
        try {
          const data = await apiRequest('/api/newbuild-profile');
          if (data.success && data.data?.profile) {
            existingProfileRef.current = data.data.profile;
          }
        } catch { /* ignore */ }
      }
      setInitReady(true);
    })();
  }

  return (
    <div className="home-page newbuild-page">
      {/* Background layers */}
      <div className="hero-orb hero-orb--1" />
      <div className="hero-orb hero-orb--2" />
      <div className="hero-orb hero-orb--3" />

      {heroImage && heroImage.enabled && heroImage.filename && (
        <div
          className="hero-bg-image"
          style={{
            backgroundImage: `url(/uploads/${heroImage.filename})`,
            opacity: heroImage.opacity / 100,
            mixBlendMode: heroImage.blend_mode as any,
            filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined,
          }}
        />
      )}

      <div className="hero-grid-pattern" />

      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Planen Sie Ihren<br />
            <span className="hero-title-gradient newbuild-gradient">Traum-Neubau</span>
          </h1>
          <p className="hero-subtitle">
            Erzählen Sie unserer AI von Ihren Wünschen und Bedürfnissen für Ihr Neubauprojekt – wir helfen Ihnen, die Machbarkeit zu prüfen und das Optimum herauszuholen.
          </p>

          {/* Chat Section */}
          <div className="chat-section">
            <div className="chat-glow" />
            <div className="chat-container">
              <div className="chat-header">
                <div className="chat-header-text">
                  <span className="chat-title">Neubau-Beratung</span>
                  <span className="chat-subtitle">AI-gestützte Bedarfsermittlung</span>
                </div>
              </div>

              <div className="chat-messages">
                {messages.filter(m => m.role !== 'system').map((msg, i) => (
                  <div key={i} className={`chat-bubble chat-bubble--${msg.role}`}>
                    {msg.role === 'assistant' && (
                      <div className="chat-avatar">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                        </svg>
                      </div>
                    )}
                    <div className="chat-content">
                      {msg.role === 'assistant' ? renderBold(msg.content) : msg.content}
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="chat-bubble chat-bubble--assistant">
                    <div className="chat-avatar">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                    </div>
                    <div className="chat-content chat-typing">
                      <span className="dot" /><span className="dot" /><span className="dot" />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="chat-error">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {error}
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Saving indicator */}
              {isSaving && (
                <div className="newbuild-saving">
                  <div className="spinner spinner--sm" /> Neubauprofil wird gespeichert...
                </div>
              )}

              {/* Success message */}
              {savedMessage && (
                <div className="newbuild-saved">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  {savedMessage}
                  <div className="newbuild-saved-actions">
                    {createdProjectId && (
                      <button className="btn btn--sm btn--primary newbuild-next-btn" onClick={() => navigate(`/newbuild/project/${createdProjectId}`)}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        Weiter: Dokumente hochladen
                      </button>
                    )}
                    <button className="btn btn--sm btn--outline newbuild-profile-btn" onClick={() => navigate('/newbuild/profile')}>
                      Zum Profil
                    </button>
                    <button className="btn btn--sm btn--outline newbuild-newchat-btn" onClick={handleNewChat}>
                      Neues Gespräch
                    </button>
                  </div>
                </div>
              )}

              {/* Input */}
              {!isComplete && (
                <div className="chat-input-area">
                  <input
                    ref={inputRef}
                    type="text"
                    className="chat-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Beschreiben Sie Ihre Vorstellungen..."
                    disabled={isLoading}
                  />
                  <button
                    className="chat-send-btn"
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    title="Senden"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </div>
              )}

              {/* Login hint for non-authenticated */}
              {!isAuthenticated && isComplete && (
                <div className="newbuild-login-hint">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#C75B4A" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Bitte <a href="/#lab-auth">melden Sie sich an</a>, um Ihr Neubauprofil zu speichern.
                </div>
              )}
            </div>
          </div>

          {/* Feature badges */}
          <div className="hero-badges">
            <div className="hero-badge hero-badge--delay-1">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              <span>Neubau-Planung</span>
            </div>
            <div className="hero-badge hero-badge--delay-2">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>AI-gestützte Analyse</span>
            </div>
            <div className="hero-badge hero-badge--delay-3">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span>Bauvorschriften-Check</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="how-it-works">
        <h2 className="how-it-works-title">So funktioniert's</h2>
        <div className="how-it-works-steps">
          <div className="how-step">
            <div className="how-step-icon how-step-icon--chat">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <h3 className="how-step-title">1. Wünsche definieren</h3>
            <p className="how-step-desc">Beschreiben Sie Ihren Traumneubau – von der Bauweise bis zur Heizung. Die AI führt Sie durch alle wichtigen Themen.</p>
          </div>

          <div className="how-step-connector">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div className="how-step">
            <div className="how-step-icon how-step-icon--search">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </div>
            <h3 className="how-step-title">2. Dokumente hochladen</h3>
            <p className="how-step-desc">Laden Sie Baureglement, Zonenplan und weitere Unterlagen hoch – die AI analysiert alles automatisch.</p>
          </div>

          <div className="how-step-connector">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div className="how-step">
            <div className="how-step-icon how-step-icon--results">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h3 className="how-step-title">3. Machbarkeit prüfen</h3>
            <p className="how-step-desc">Erfahren Sie, was realisierbar ist und was das maximale Baupotenzial auf Ihrem Grundstück wäre.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
